/* space-review-gallery-v2.js
   Plain click-triggered class toggles only. No scroll-linked rAF loops, no IntersectionObserver-based
   reveal animation. This keeps the section safe inside the Facebook/Instagram in-app browser, which
   suspends/throttles background rendering work and can leave JS-gated content permanently hidden. */

(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function starString(rating) {
    var r = Math.round(Number(rating) || 0);
    var full = '★'.repeat(Math.max(0, Math.min(5, r)));
    var empty = '☆'.repeat(Math.max(0, 5 - r));
    return full + empty;
  }

  function initials(name) {
    var n = (name || '').trim();
    return n ? n.charAt(0).toUpperCase() : '?';
  }

  // PERF: our review photos are hosted on Shopify's CDN (Content > Files), which
  // supports on-the-fly resizing via a `width` query param — e.g. adding
  // `?width=300` to a file URL returns a much smaller, already-resized JPEG
  // instead of the full original (confirmed live: a 1224x1632 / ~104KB original
  // shrinks to 300x400 / ~24KB with this param, ~4x less data transferred).
  // Grid thumbnails only ever render at a small square size, so requesting the
  // full original for all 12+ images on every page load was pure waste and
  // was slowing down the page. This helper appends the resize param safely
  // (handles URLs that may already have a query string).
  function shopifyResize(url, width) {
    if (!url) return url;
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + width;
  }

  // Fixed category priority order (matches the filter pill order).
  var CATEGORY_ORDER = [
    'Kitchen & Pantry', 'Laundry Room', 'Hobby & Collection', 'Pet & Plant',
    'Utility Room', 'Workspace', 'Garage', 'Others'
  ];

  // "All" tab: instead of showing items grouped block-by-block by category,
  // interleave them round-robin — one from each category in priority order,
  // repeating until every category is exhausted. Row order WITHIN each
  // category is preserved (the original excel row order).
  function interleaveByCategory(items) {
    var buckets = {};
    CATEGORY_ORDER.forEach(function (cat) { buckets[cat] = []; });
    var leftover = [];
    items.forEach(function (it) {
      if (buckets.hasOwnProperty(it.space)) buckets[it.space].push(it);
      else leftover.push(it);
    });
    var maxLen = 0;
    CATEGORY_ORDER.forEach(function (cat) {
      if (buckets[cat].length > maxLen) maxLen = buckets[cat].length;
    });
    var result = [];
    for (var i = 0; i < maxLen; i++) {
      CATEGORY_ORDER.forEach(function (cat) {
        if (buckets[cat][i]) result.push(buckets[cat][i]);
      });
    }
    return result.concat(leftover);
  }

  function initSection(cfg) {
    var sectionId = cfg.sectionId;
    var root = document.getElementById('srg-' + sectionId);
    if (!root) return;

    var grid = document.getElementById('srg-grid-' + sectionId);
    var moreBtn = document.getElementById('srg-more-' + sectionId);
    var modal = document.getElementById('srg-modal-' + sectionId);
    var modalImg = document.getElementById('srg-modal-img-' + sectionId);
    var modalStars = document.getElementById('srg-modal-stars-' + sectionId);
    var modalAvatar = document.getElementById('srg-modal-avatar-' + sectionId);
    var modalName = document.getElementById('srg-modal-name-' + sectionId);
    var modalDate = document.getElementById('srg-modal-date-' + sectionId);
    var modalTitle = document.getElementById('srg-modal-title-' + sectionId);
    var modalBody = document.getElementById('srg-modal-body-' + sectionId);
    var modalProduct = document.getElementById('srg-modal-product-' + sectionId);
    var modalCta = document.getElementById('srg-modal-cta-' + sectionId);

    var allItems = [];
    var visibleItems = [];   // items matching the active filter (full set)
    var shownItems = [];     // subset of visibleItems currently rendered (paged)
    var activeFilter = 'all';
    var currentIndex = -1;
    var visibleCount = 0;

    function getColumnCount() {
      var cols = getComputedStyle(grid).gridTemplateColumns.trim().split(/\s+/).filter(Boolean).length;
      return cols || 6;
    }
    function getStep() {
      var cols = getColumnCount();
      var rows = cols <= 2 ? 3 : 2; // mobile (2 cols): 3 rows = 6. tablet/desktop: 2 rows.
      return cols * rows;
    }

    function openModalAt(idx) {
      if (idx < 0 || idx >= shownItems.length) return;
      currentIndex = idx;
      var item = shownItems[idx];

      // Modal shows a larger image (up to ~920px wide), but original photos can be
      // 1500px+ — cap at 1200 so we're not shipping multi-MB originals into a popup.
      modalImg.src = shopifyResize(item.image, 1200) || '';
      modalImg.alt = item.title || '';
      modalStars.textContent = starString(item.rating);
      modalAvatar.textContent = initials(item.reviewer);
      modalName.textContent = item.reviewer || '';
      modalDate.textContent = item.date || '';
      modalTitle.textContent = item.title || '';
      modalBody.textContent = item.body || '';

      var productBits = [];
      if (item.product) productBits.push('Product : ' + item.product);
      if (item.size) productBits.push('Size : ' + item.size);
      if (item.color) productBits.push('Color : ' + item.color);
      modalProduct.innerHTML = productBits.map(function (b) { return escapeHtml(b); }).join('<br>');

      if (item.productHandle && cfg.shopBase) {
        modalCta.href = cfg.shopBase.replace(/\/$/, '') + '/products/' + item.productHandle;
        modalCta.style.display = '';
      } else {
        modalCta.removeAttribute('href');
        modalCta.style.display = 'none';
      }

      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
    }

    function showPrev() { openModalAt((currentIndex - 1 + shownItems.length) % shownItems.length); }
    function showNext() { openModalAt((currentIndex + 1) % shownItems.length); }

    modal.addEventListener('click', function (e) {
      if (e.target.hasAttribute('data-srg-close')) closeModal();
      if (e.target.closest('[data-srg-prev]')) showPrev();
      if (e.target.closest('[data-srg-next]')) showNext();
    });
    document.addEventListener('keydown', function (e) {
      if (!modal.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    });

    function renderGrid() {
      shownItems = visibleItems.slice(0, visibleCount);
      if (!shownItems.length) {
        grid.innerHTML = '<p class="srg__empty">No photos yet.</p>';
        moreBtn.hidden = true;
        return;
      }
      grid.innerHTML = shownItems.map(function (item, i) {
        var small = shopifyResize(item.image, 300);
        var large = shopifyResize(item.image, 600);
        return (
          '<button type="button" class="srg__item" data-idx="' + i + '">' +
            '<span class="srg__item-crop">' +
              '<img src="' + escapeHtml(small) + '" srcset="' + escapeHtml(small) + ' 1x, ' + escapeHtml(large) + ' 2x" ' +
                'alt="' + escapeHtml(item.title) + '" loading="lazy" decoding="async">' +
            '</span>' +
          '</button>'
        );
      }).join('');
      moreBtn.hidden = visibleCount >= visibleItems.length;
    }

    function render() {
      if (activeFilter === 'all') {
        visibleItems = interleaveByCategory(allItems);
      } else {
        visibleItems = allItems.filter(function (it) { return it.space === activeFilter; });
      }
      visibleCount = Math.min(getStep(), visibleItems.length);
      renderGrid();
    }

    grid.addEventListener('click', function (e) {
      var btn = e.target.closest('.srg__item');
      if (!btn) return;
      var idx = parseInt(btn.getAttribute('data-idx'), 10);
      openModalAt(idx);
    });

    moreBtn.addEventListener('click', function () {
      visibleCount += getStep();
      renderGrid();
    });

    var filterBtns = root.querySelectorAll('.srg__filter-btn');
    filterBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        filterBtns.forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        activeFilter = btn.getAttribute('data-space');
        render();
      });
    });

    fetch(cfg.dataUrl).then(function (r) { return r.ok ? r.json() : []; }).then(function (data) {
      allItems = data || [];
      render();
    }).catch(function () {
      grid.innerHTML = '<p class="srg__empty">Could not load photos.</p>';
    });

    var lastCols = null;
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var cols = getColumnCount();
        if (cols !== lastCols) {
          lastCols = cols;
          if (allItems.length) render();
        }
      }, 200);
    });
  }

  ready(function () {
    (window.__srgSections || []).forEach(initSection);
  });
})();